import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Table,
  Button,
  Select,
  Dialog,
  TableRow,
  MenuItem,
  TextField,
  TableCell,
  TableBody,
  Switch,
  InputLabel,
  FormControl,
  FormControlLabel,
  DialogActions,
  TableContainer,
  CircularProgress,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import thaAxios from "../../../utils/thaAxiosService";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";
import { getProductTypeNameByNumber } from "../../../utils";

const PopularityRank = () => {
  const agGridRef = useRef<AgGridReact | null>(null);
  const [simData, setSimData] = useState<any[]>([]);
  const [countryListN, setCountryListN] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOperatorLoading, setIsOperatorLoading] = useState<boolean>(false);
  const [origination, setOrigination] = useState<any>("0");
  const [destination, setDestination] = useState<any>("0");
  const [operator, setOperator] = useState<any>("0");
  const [operatorList, setOperatorList] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [perform, setPerform] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [displayOrder, setDisplayOrder] = useState<string>("");

  const loadCountries = async () => {
    await fetch("/data/thacountry.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json: any) => {
        setCountryListN(json.thacountries);
      });
  };

  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };

  const getCountryNameByIsoCode = (isoCode: string) => {
    return (
      countryListN.find(
        (item) => item.isoCode2?.toLowerCase() === isoCode?.toLowerCase(),
      )?.countryName || isoCode
    );
  };

  const formatDateTime = (dateValue: string) => {
    if (!dateValue) return "";

    const normalizedDate = dateValue.replace(
      /T(\d{2}:\d{2}:\d{2}):(\d{3})$/,
      "T$1.$2",
    );
    const parsedDate = dayjs(normalizedDate);

    return parsedDate.isValid()
      ? parsedDate.format("DD-MM-YYYY hh:mm A")
      : dateValue;
  };

  const getApiErrorMessage = (error: any) => {
    const errors = error?.response?.data?.errors;

    if (errors) {
      const errorMessages = Object.values(errors).flat();

      if (errorMessages.length > 0) {
        return errorMessages.join(" ");
      }
    }
  };

  const getOperatorShortCode = (rowData: any) => {
    return (
      rowData?.operatorShortCode ||
      rowData?.OperatorShortCode ||
      rowData?.productOperator?.operatorShortCode ||
      ""
    );
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    setOperator("0");
    setOperatorList([]);

    if (destination !== "0" && destination !== undefined) {
      getOperator();
    }
  }, [destination]);

  const sortOperators = (operators: any[]) => {
    return operators.sort((a, b) =>
      a.operator?.operatorName.localeCompare(b.operator?.operatorName),
    );
  };

  const getOperator = async () => {
    setIsOperatorLoading(true);
    toast.dismiss();

    await axiosInterceptorInstance
      .post(`/FusionHubCrm/GetOperators`, {
        page: 1,
        recordsPerPage: 10000,
        operatorFilters: {
          countryIsoCode: destination.isoCode2,
        },
      })
      .then((response: any) => {
        const responseData = response?.data.payload || [];
        const operators = responseData.map((item: any) => ({
          operator: item.operator,
          country: item.country,
        }));
        const sortedOperators = sortOperators(operators);
        setOperatorList(sortedOperators);

        if (sortedOperators.length === 0) {
          toast.info("No operator found");
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsOperatorLoading(false);
      });
  };

  const getProductData = () => {
    toast.dismiss();
    getDisplayOrderRules();
  };

  const getDisplayOrderRules = async () => {
    setIsLoading(true);
    setSimData([]);

    const queryParams = new URLSearchParams();
    if (origination !== "0" && origination !== undefined) {
      queryParams.append("origination", origination.isoCode2);
    }
    if (destination !== "0" && destination !== undefined) {
      queryParams.append("destination", destination.isoCode2);
    }
    if (operator !== "0" && operator !== undefined) {
      queryParams.append(
        "operatorShortCode",
        operator.operator.operatorShortCode,
      );
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `CRMProductDisplayOrderRules/GetByOriginationDestination?${queryString}`
      : "CRMProductDisplayOrderRules/GetByOriginationDestination";

    await thaAxios
      .get(url)
      .then((response: any) => {
        const responseData = response.data;
        if (responseData.payload) {
          setSimData(responseData.payload);
          if (responseData.payload.length === 0) {
            toast.info("No record found");
          }
        } else {
          toast.error(responseData.message);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const checkUpdation = () => {
    toast.dismiss();

    if (displayOrder === "") {
      toast.error("DisplayOrder is required");
    } else {
      updateDisplayOrder();
    }
  };

  const handleDisplayOrderChange = (value: string) => {
    if (value === "") {
      setDisplayOrder("");
      return;
    }

    const displayOrderNumber = Number(value);

    if (Number.isNaN(displayOrderNumber)) return;

    if (displayOrderNumber < 0) {
      setDisplayOrder("0");
      return;
    }

    if (displayOrderNumber > simData.length) {
      setDisplayOrder(String(simData.length));
      return;
    }

    setDisplayOrder(value);
  };

  const updateDisplayOrder = async () => {
    setPerform(true);

    await thaAxios
      .post("CRMProductDisplayOrderRules/UpsertProductDisplayOrderRules", {
        name: selectedRowData.name,
        productType: selectedRowData.productType,
        origination: selectedRowData.origination,
        destination: selectedRowData.destination,
        OperatorShortCode: getOperatorShortCode(selectedRowData),
        productId: selectedRowData.productId,
        isActive: selectedRowData.isActive,
        displayOrder: Number(displayOrder),
      })
      .then((res: any) => {
        const data = res.data;
        setSimData((prevData: any[]) =>
          prevData.map((item: any) =>
            item.id === selectedRowData.id
              ? {
                  ...item,
                  displayOrder: Number(displayOrder),
                }
              : item,
          ),
        );
        toast.success(data.message || "Display order updated successfully");
        setOpenDialog(false);
      })
      .catch((error: any) => {
        toast.error(getApiErrorMessage(error));
        console.error(error);
      })
      .finally(() => {
        setPerform(false);
      });
  };

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "name",
      headerName: "Name",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "productType",
      headerName: "ProductType",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (params) =>
        getProductTypeNameByNumber(Number(params.value)) || params.value,
    },
    {
      field: "origination",
      headerName: "Origination",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (params) => getCountryNameByIsoCode(params.value),
    },
    {
      field: "destination",
      headerName: "Destination",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (params) => getCountryNameByIsoCode(params.value),
    },
    {
      field: "operatorShortCode",
      headerName: "Operator Short Code",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "productId",
      headerName: "ProductId",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "isActive",
      headerName: "IsActive",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "createdAt",
      headerName: "CreatedAt",
      filter: true,
      sortable: true,
      resizable: true,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      field: "displayOrder",
      headerName: "DisplayOrder",
      filter: true,
      sortable: true,
      resizable: true,
    },
  ];

  const onRowClicked = (event: { data: any }) => {
    const rowData = event.data;
    setSelectedRowData(rowData);
    setDisplayOrder(rowData.displayOrder);
    setOpenDialog(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Popularity Rank</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <fieldset>
                <legend>General Information:</legend>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    {countryListN && (
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          id="origination-select-label"
                          style={{ background: "#fff", padding: " 0 5px" }}
                        >
                          Origination
                        </InputLabel>
                        <Select
                          labelId="origination-select-label"
                          id="origination-select"
                          className="formControl"
                          autoWidth
                          value={origination?.isoCode2 || 0}
                          defaultValue={origination?.isoCode2 || 0}
                          onChange={(e: any) => {
                            const country = countryListN?.find(
                              (item) => item.isoCode2 === e.target.value,
                            );
                            setOrigination(country || "0");
                          }}
                        >
                          <MenuItem value="0">Select Origination</MenuItem>
                          {countryListN.map((item, index) => (
                            <MenuItem key={index} value={item?.isoCode2}>
                              {item?.countryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    {countryListN && (
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          id="destination-select-label"
                          style={{ background: "#fff", padding: " 0 5px" }}
                        >
                          Destination
                        </InputLabel>
                        <Select
                          labelId="destination-select-label"
                          id="destination-select"
                          className="formControl"
                          autoWidth
                          value={destination?.isoCode2 || 0}
                          defaultValue={destination?.isoCode2 || 0}
                          onChange={(e: any) => {
                            const country = countryListN?.find(
                              (item) => item.isoCode2 === e.target.value,
                            );
                            setDestination(country || "0");
                          }}
                        >
                          <MenuItem value="0">Select Destination</MenuItem>
                          {countryListN.map((item, index) => (
                            <MenuItem key={index} value={item?.isoCode2}>
                              {item?.countryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        id="operator-select-label"
                        style={{ background: "#fff", padding: " 0 5px" }}
                      >
                        Operator
                      </InputLabel>

                      <Select
                        labelId="operator-select-label"
                        id="operator-select"
                        className="formControl"
                        autoWidth
                        disabled={
                          isOperatorLoading ||
                          destination === "0" ||
                          destination === undefined
                        }
                        value={operator?.operator?.operatorId || "0"}
                        defaultValue={operator?.operator?.operatorId || "0"}
                        onChange={(e: any) => {
                          const selectedOperator = operatorList?.find(
                            (item: any) =>
                              item.operator?.operatorId === e.target.value,
                          );

                          setOperator(selectedOperator || "0");
                        }}
                      >
                        <MenuItem value="0">Choose Type</MenuItem>

                        {isOperatorLoading ? (
                          <div
                            className="circular-loader"
                            style={{ marginLeft: "0" }}
                          >
                            <CircularProgress size={40} />
                          </div>
                        ) : (
                          operatorList.map((item: any, index: any) => (
                            <MenuItem
                              key={index}
                              value={item.operator?.operatorId}
                            >
                              {item.operator?.operatorName}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid>
                  <Button
                    variant="contained"
                    onClick={getProductData}
                    disabled={isOperatorLoading}
                    sx={{
                      margin: "0 auto",
                      display: "block",
                      width: "280px !important",
                    }}
                  >
                    Submit
                  </Button>
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
        </div>
      </div>

      {isLoading && (
        <div className="circular-loader" style={{ marginLeft: "0px" }}>
          <CircularProgress size={40} />
        </div>
      )}
      <div>
        <button
          className="small-btn"
          style={{
            visibility: simData?.length > 0 ? "visible" : "hidden",
            marginBottom: "10px",
          }}
          onClick={() => {
            if (simData?.length > 0) {
              exportGridDataAsCSV();
            }
          }}
        >
          Export as CSV
        </button>
      </div>
      {simData?.length > 0 && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={12}
            pagination={true}
            domLayout="autoHeight"
            rowData={simData}
            columnDefs={columnDefs}
            onRowClicked={onRowClicked}
            onGridReady={onGridReady}
          ></AgGridReact>
        </div>
      )}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box style={{ padding: "0 16px " }}>
          <h1 className="heading" style={{ marginTop: "20px" }}>
            Updation Confirmation
          </h1>
          {selectedRowData && (
            <TableContainer
              component={Paper}
              style={{
                marginTop: "20px",
                marginBottom: "20px",
                padding: "0px",
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>{selectedRowData.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>ProductType</strong>
                    </TableCell>
                    <TableCell>
                      {getProductTypeNameByNumber(
                        Number(selectedRowData.productType),
                      ) || selectedRowData.productType}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Origination</strong>
                    </TableCell>
                    <TableCell>
                      {getCountryNameByIsoCode(selectedRowData.origination)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Destination</strong>
                    </TableCell>
                    <TableCell>
                      {getCountryNameByIsoCode(selectedRowData.destination)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Operator Short Code</strong>
                    </TableCell>
                    <TableCell>
                      {getOperatorShortCode(selectedRowData)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>ProductId</strong>
                    </TableCell>
                    <TableCell>{selectedRowData.productId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>DisplayOrder</strong>
                    </TableCell>
                    <TableCell>{selectedRowData.displayOrder}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>IsActive</strong>
                    </TableCell>
                    <TableCell>
                      {selectedRowData.isActive ? "Yes" : "No"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TextField
            className="formControl"
            style={{ marginTop: "20px" }}
            label="DisplayOrder"
            variant="outlined"
            type="number"
            value={displayOrder}
            inputProps={{ min: 0, max: simData.length }}
            onKeyDown={(e) => {
              if (["-", "+", "e", "E"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => handleDisplayOrderChange(e.target.value)}
          />

          <DialogActions>
            <Button
              variant="contained"
              className="dButton dButton2 dbuttonSec"
              onClick={() => setOpenDialog(false)}
            >
              No
            </Button>
            <div style={{ width: "30px" }}></div>
            {!perform && (
              <Button
                variant="contained"
                className="dButton dButton2"
                onClick={() => {
                  checkUpdation();
                }}
                autoFocus
              >
                Yes
              </Button>
            )}
            {perform && (
              <Button className="dButton dButton2" variant="contained">
                <CircularProgress color="inherit" size={30} />
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </motion.div>
  );
};

export default PopularityRank;
